/**
 * Achievement Icon Component
 * Renders the appropriate SVG icon based on achievement level
 */

import React from 'react'
import { Box } from '@mui/material'
import { AchievementLevel } from '../types/achievementTypes'
import { AchievementType as BEAchievementType } from 'pure-js/types/Achievement'

export interface AchievementIconProps {
  level?: AchievementLevel
  achievementId?: BEAchievementType | string
  size?: number
  className?: string
}

/**
 * AchievementIcon - Renders achievement badge icon based on level
 * @param level - Achievement level (Bronze, Silver, Gold, or undefined for not achieved)
 * @param achievementId - Achievement ID from BE (optional, for special icons like uridium_collector)
 * @param size - Icon size in pixels (default: 110)
 * @param className - Additional CSS classes
 */
export const AchievementIcon: React.FC<AchievementIconProps> = ({
  level,
  achievementId,
  size = 110,
  className = ''
}) => {
  const getIconPath = (): string => {
    // Special case: Uridium Collector uses crystal_image
    if (achievementId === BEAchievementType.URIDIUM_COLLECTOR || achievementId === 'uridium_collector') {
      return '/assets/img/crystal_image.png'
    }

    switch (level) {
      case AchievementLevel.BRONZE:
        return '/assets/svg/achieved_bronze.svg'
      case AchievementLevel.SILVER:
        return '/assets/svg/achieved_silver.svg'
      case AchievementLevel.GOLD:
      case AchievementLevel.PLATINUM:
        return '/assets/svg/achieved.svg'
      default:
        return '/assets/svg/not_achieved.svg'
    }
  }

  const getAltText = (): string => {
    switch (level) {
      case AchievementLevel.BRONZE:
        return 'Bronze achievement'
      case AchievementLevel.SILVER:
        return 'Silver achievement'
      case AchievementLevel.GOLD:
        return 'Gold achievement'
      case AchievementLevel.PLATINUM:
        return 'Platinum achievement'
      default:
        return 'Achievement not completed'
    }
  }

  return (
    <Box
      component="img"
      src={getIconPath()}
      alt={getAltText()}
      className={className}
      sx={{
        width: size,
        height: size,
        display: 'block',
        objectFit: 'contain'
      }}
    />
  )
}
